/**
 * Navigation, callable from outside React — §4.1.
 *
 * "Which page is on screen" is owned by the URL, so turning a page is a router call.
 * `lib/commands.ts` is plain functions with no hooks, and it still has to be able to
 * turn a page (a search hit, a reciter jump), so it needs the router without
 * `useRouter()`.
 *
 * THIS IS A REGISTRY, NOT AN IMPORT OF THE ROUTER, and that is deliberate: creating the
 * router in a module that commands import would make the cycle
 *
 *     lib/router → routeTree.gen → routes/read.$page → lib/commands → lib/router
 *
 * and the route modules would evaluate against a half-initialised binding. `main.tsx`
 * creates the router and registers it here, so nothing in this file imports a route.
 */
import { clampPage } from '@mushaf/corpus'
import type { AnyRouter } from '@tanstack/react-router'

let router: AnyRouter | null = null

export const registerRouter = (r: AnyRouter) => {
  router = r
}

/** The page the URL currently names, or `null` when the reader is not the active route. */
export function currentPage(): number | null {
  const params = router?.state.matches.at(-1)?.params as { page?: string } | undefined
  return params?.page === undefined ? null : clampPage(Number(params.page))
}

/**
 * Turn the view to a page.
 *
 * CLAMPED, never rejected: a hand-typed /read/9999 still renders (§6), and clamping
 * here is what lets the page arrows skip their `disabled` attribute (T65) — at 1 and
 * 604 the press resolves to the page already on screen and is dropped below.
 */
export function goToPage(next: number) {
  if (!router) return
  const target = clampPage(next)
  if (target === currentPage()) return
  void router.navigate({ to: '/read/$page', params: { page: String(target) } })
}
